import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { AutomationEvent, FieldDefinition } from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import ApiError from "~/plugins/errors/apiError";
import type { MaybeRefOrGetter } from "vue";
import {
    faRepeat,
} from "@fortawesome/pro-regular-svg-icons";

export default class EventService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<AutomationEvent> {
        return this.paginate<AutomationEvent>(
            this.route("api.events.index", "/events"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        id: number,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<AutomationEvent> {
        return this.enhancedFetch<AutomationEvent>(
            this.route("api.events.show", "/events/{event}", {
                event: id,
            }),
            options,
        );
    }

    public findRequest(
        id: number,
        options?: EnhancedFetchOptions,
    ): Promise<ApiResponse<AutomationEvent>> {
        return this.request<ApiResponse<AutomationEvent>>(
            this.route("api.events.show", "/events/{event}", {
                event: id,
            }),
            options,
        );
    }

    public update(
        id: number,
        data: Partial<AutomationEvent>,
    ): Promise<ApiResponse<AutomationEvent>> {
        return this.request(
            this.route("api.events.store", "/events/{event}", {
                event: id,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.events.destroy", "/events/{event}", {
                event: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public replay(id: number, toast: boolean = true): Promise<null> {
        return this.request(
            this.route("api.events.replay", "/events/{event}/replay", {
                event: id,
            }),
            {
                method: "POST",
            },
        )
            .then((e) => {
                if (toast) {
                    useToast().add({
                        title: "Event Replayed",
                    });
                }

                return e;
            })
            .catch((e: ApiError) => {
                if (toast) {
                    useToast().add({
                        ...e.errors,
                        title: e.message,
                        icon: "i-heroicons-exclamation-triangle",
                        color: "red",
                    });
                } else {
                    throw e;
                }

                return e;
            });
    }

    public replayBulk(ids: number[]) {
        return Promise.allSettled(ids.map((id) => this.replay(id, false))).then(
            (result) => {
                const fulfilled = result.filter(
                    (res) => res.status === "fulfilled",
                );
                const rejected = result.filter(
                    (res) => res.status === "rejected",
                );

                if (fulfilled.length) {
                    useToast().add({
                        title: `${fulfilled.length} Events Replayed`,
                    });
                }

                if (rejected.length) {
                    useArrayUnique(
                        rejected.map((rej) => rej.reason.message),
                    ).value.forEach((msg) => {
                        const error: { reason: ApiError } | undefined = (
                            rejected as unknown as { reason: ApiError }[]
                        ).find((rej) => rej.reason.message === msg);

                        useToast().add({
                            ...(error ? (error.reason.errors ?? {}) : {}),
                            title: msg,
                            icon: "i-heroicons-exclamation-triangle",
                            color: "red",
                        });
                    });
                }

                return result;
            },
        );
    }

    public dropdown(event: Event, details: boolean = false) {
        let items: any[] = [];

        if (details) {
            items = [
                {
                    label: "Replay",
                    icon: faRepeat,
                    click: () => this.replay(event.id),
                },
            ];
        } else {
            items = [
                {
                    label: "View",
                    to: {
                        name: "settings-events-id",
                        params: { id: event.id },
                    },
                },
                { label: "Replay", click: () => this.replay(event.id) },
            ];
        }

        return [items];
    }

    public processes(
        eventId: MaybeRefOrGetter<number>,
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Event[]> {
        return this.paginate<Event[]>(
            () =>
                this.route("api.events.process", "/events/{id}/processes", {
                    id: toRef(eventId).value,
                }),
            options as PaginatedFetchOptions,
        );
    }

    public object(eventId: MaybeRefOrGetter<number>): Promise<
        ApiResponse<{
            app: {
                label: string;
                icon: string;
                logo: string;
            };
            object: {
                object: string;
                label: string;
                label_plural: string;
            };
            fields: FieldDefinition[];
        }>
    > {
        return this.request<
            ApiResponse<{
                app: {
                    label: string;
                    icon: string;
                    logo: string;
                };
                object: {
                    object: string;
                    label: string;
                    label_plural: string;
                };
                fields: FieldDefinition[];
            }>
        >(
            this.route("api.events.object", "/events/{id}/object", {
                id: toRef(eventId).value,
            }),
        );
    }
}
